import { useState } from "react";

import type { DiagnosticRun } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  useApproveDiagnostic,
  useRejectDiagnostic,
} from "@/hooks/useDiagnosticRuns";

interface Props {
  run: DiagnosticRun;
  sessionId: string;
}

const RESOLVED_STATUSES = new Set([
  "approved",
  "rejected",
  "executed",
  "failed",
  "timeout",
]);

export function DiagnosticProposalCard({ run, sessionId }: Props) {
  const approveMutation = useApproveDiagnostic(sessionId);
  const rejectMutation = useRejectDiagnostic(sessionId);
  const [confirming, setConfirming] = useState<"approve" | null>(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reason, setReason] = useState("");

  const isResolved = RESOLVED_STATUSES.has(run.status);

  const handleApprove = () => {
    if (confirming !== "approve") {
      setConfirming("approve");
      return;
    }
    approveMutation.mutate(run.id);
    setConfirming(null);
  };

  const handleReject = () => {
    rejectMutation.mutate({ runId: run.id, reason: reason.trim() || undefined });
    setShowRejectForm(false);
    setReason("");
  };

  return (
    <div className="rounded-lg border border-sky-300 bg-sky-50 p-4 dark:border-sky-700 dark:bg-sky-950/40">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <span className="rounded bg-sky-200 px-2 py-0.5 text-xs uppercase tracking-wide text-sky-900 dark:bg-sky-800 dark:text-sky-100">
          diagnóstico · {run.status}
        </span>
        <span className="font-mono text-xs">{run.command_name}</span>
        <span className="text-xs text-muted-foreground">en {run.host}</span>
      </div>
      <div className="mb-2 text-sm">
        <span className="font-medium">Razón:</span> {run.reason}
      </div>
      <div className="mb-2 rounded bg-slate-900/80 p-2 font-mono text-xs text-slate-100 dark:bg-slate-950">
        {run.rendered_command}
      </div>

      {(run.status === "executed" ||
        run.status === "failed" ||
        run.status === "timeout") && (
        <div className="mt-2 rounded border bg-card p-2">
          <div className="mb-1 text-xs text-muted-foreground">
            Resultado · exit={run.exit_code ?? "?"}
            {run.duration_ms !== null && <> · {run.duration_ms}ms</>}
            {run.output_truncated && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                (output truncado)
              </span>
            )}
          </div>
          {run.error && (
            <div className="mb-1 text-xs text-destructive">
              Error: {run.error}
            </div>
          )}
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-2 font-mono text-xs">
            {run.output ?? "(sin output)"}
          </pre>
        </div>
      )}

      {run.status === "rejected" && run.error && (
        <div className="mt-2 text-xs text-muted-foreground">
          Razón del rechazo: {run.error}
        </div>
      )}

      {!isResolved && (
        <div className="mt-3 space-y-2">
          {confirming === "approve" && (
            <div className="rounded border border-sky-400 bg-sky-100 p-2 text-xs dark:bg-sky-900/40">
              Confirma: AROC ejecutará{" "}
              <span className="font-mono">{run.command_name}</span> en{" "}
              <span className="font-mono">{run.host}</span>. Comando de solo
              lectura.
            </div>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {confirming === "approve" ? "Sí, ejecutar" : "Aprobar"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowRejectForm((v) => !v)}
              disabled={rejectMutation.isPending}
            >
              Rechazar
            </Button>
            {confirming === "approve" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirming(null)}
              >
                Cancelar
              </Button>
            )}
          </div>
          {showRejectForm && (
            <div className="space-y-2 rounded border bg-background p-2">
              <label className="text-xs font-medium">
                Razón (opcional, alimenta a AROC para que ajuste su estrategia)
              </label>
              <textarea
                className="min-h-[60px] w-full resize-none rounded-md border bg-background p-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: el host está en mantenimiento."
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                >
                  Confirmar rechazo
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowRejectForm(false);
                    setReason("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
